import { memo, useCallback, useContext, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import ProductHeader from "./ProductHeader/ProductHeader.jsx";
import BackgroundProgressiveImg from "app/pages/.shared/BackgroundProgressiveImg.jsx";
import isEmpty from "lodash/isEmpty";
import { FormattedMessage, FormattedPlural } from "react-intl";
import { ficheProduitPropTypes, photoPropTypes, videoPropTypes } from "app/utils/propTypes";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { BRANDS, QUOTATION_PRODUCT_EXPERIENCE, SLIDE_SHOW_PHOTO_TYPES } from "app/constants";
import List from "app/pages/.shared/List";
import PropTypes from "prop-types";
import SocialBarPopover from "app/pages/.shared/social/SocialBarPopover";
import "./FicheProduit.scss";
import ProductActionButtonMobile from "app/pages/FicheProduit/components/ProductActionButtonMobile/ProductActionButtonMobile";
import get from "lodash/get";
import IconPicture from "app/pages/.shared/static/icons/IconPicture";
import { PushNotificationContainer } from "app/pages/.shared/PushNotification/PushNotificationContainer";
import DealTimer from "app/pages/.shared/Deal/DealTimer/DealTimer";
import { sendTagOnOpenProductCarouselDesktop } from "app/utils/analytics.js";
import Collapse from "rc-collapse";
import LookAlikeProductsContainer from "app/pages/.shared/LookAlikeProduct/LookAlikeProductsContainer";
import OffersOverview from "app/pages/.shared/OffersOverview/OffersOverview";
import AvailableDurations from "app/pages/.shared/AvailableDurations";
import BookingBannerPlaceholderContainer from "app/pages/Booking/BookingBannerPlaceholder/BookingBannerPlaceholderContainer";
import NegociatedList from "app/pages/FicheProduit/NegociatedList/NegociatedList";
import IconShare from "app/pages/.shared/IconShare";
import { useIntersectionObserver } from "app/hooks";
import AsideOffersOverview from "app/pages/FicheProduit/AsideOffersOverview/AsideOffersOverview";
import { scrollToElement } from "app/utils/scroller";
import loadable from "@loadable/component";
import IconGoToTopArrow from "app/pages/.shared/static/icons/IconGoToTopArrow";
import AppGlobalsContext from "app/AppGlobalsContext";
import CondensedBookWithConfidence from "app/pages/.shared/BookWithConfidence/CondensedBookWithConfidence";
import HeaderContainer from "app/pages/Header/HeaderContainer";
import AdditionalSections from "app/pages/FicheProduit/components/AdditionalSections/AdditionalSections";
import FullyCustomizableNote from "app/pages/.shared/FullyCustomizableNote/FullyCustomizableNote";
import PrintHeader from "./PrintHeader/PrintHeader";
import ProductRatingContainer from "app/pages/FicheProduit/components/ProductRating/ProductRatingContainer";
import TotalCreditBannerContainer from "app/pages/FicheProduit/components/Credit/TotalCreditBannerContainer";
import { isFlashsaleProduct, redirectToShopUrl } from "app/utils/utils";
import ProductNameWithCategory from "app/pages/.shared/ProductNameWithCategory/ProductNameWithCategory.jsx";
import ReasonToBookBlockContainer from "app/pages/.shared/ReasonsToBook/ReasonToBookBlock/ReasonToBookBlockContainer";
import FicheProduitHeadMetadata from "app/pages/FicheProduit/components/FicheProduitHeadMetadata";
import AvisVerifiesContainer from "app/pages/.shared/AvisVerifies/AvisVerifiesContainer";
import ContactUs from "app/pages/.shared/ContactUs/ContactUs";
import AccommodationSection from "app/pages/FicheProduit/components/Accommodation/AccommodationSection";
import AccommodationTypes from "app/pages/FicheProduit/components/Accommodation/AccommodationTypes";
import Amenities from "app/pages/FicheProduit/components/Amenities/Amenities";
import RestaurantSection from "app/pages/FicheProduit/components/Restaurant/RestaurantSection";
import Activities from "app/pages/FicheProduit/components/Activities/Activities";
import TransportPartnership from "app/pages/FicheProduit/components/TransportPartnership/TransportPartnership";
import Destination from "app/pages/FicheProduit/components/Destination/Destination";
import UsefulInformation from "app/pages/FicheProduit/components/UsefulInformation/UsefulInformation";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import LookAlikeSearchContentContainer from "app/pages/.shared/LookAlikeProduct/LookAlikeSearchContentContainer";
import ProductBadge from "app/features/product/components/ProductBadge/ProductBadge";
import PaymentDescriptionContainer from "app/pages/.shared/DynamicBlocks/blocks/PaymentDescriptionBlock/PaymentDescriptionContainer";
import SearchEngineCreditBannerContainer from "app/pages/PrePackage/components/SearchEngineCreditBanner/SearchEngineCreditBannerContainer";
import { useFsFlag } from "@flagship.io/react-sdk";
import IconArrowDown from "app/pages/.shared/static/icons/IconArrowDown";

const SearchEngineContainer = loadable(() =>
	import(/* webpackChunkName: "product-page-search-engine" */ "app/pages/PrePackage/SearchEngineContainer.jsx")
);

const PhotoCarousel = loadable(() =>
	import(/* webpackChunkName: "product-page-photo-caroussel" */ "app/pages/.shared/PhotoCarousel.jsx")
);

const SlideShow = loadable(() =>
	import(/* webpackChunkName: "product-page-slideshow" */ "app/pages/.shared/Slideshow/SlideShow.jsx")
);

const HERO_IMG_CLOUDINARY_OPTIONS = ["c_fill"];

const HERO_IMG_SIZES = {
	medium: { height: 460 },
	large: { height: 460 },
	xlarge: { height: 460 },
};

const HOTEL_PANEL_INDEX = "0";
const ROOMS_PANEL_INDEX = "1";
const RESTURANTS_PANEL_INDEX = "2";
const ACTIVITIES_PANEL_INDEX = "3";

const FicheProduit = ({
	resolution,
	ficheProduit = {},
	prePackageOffers = [],
	closeSlideshow,
	slideshow = {},
	negociated = [],
	allAvailableDurations,
	currentShop = "",
	partnerCode,
	mobileSlideshowPhotos = [],
	desktopSlideShowPhotos = [],
	videos = [],
	shouldFetchLookAlikeProduts,
	specialOffer = {},
	openSlideshow,
	isWhiteLabel,
	googleAPIKey,
	showPfsMessage,
	updateBooking,
	productPriceValue,
}) => {
	const [showStickyHeader, toggleStickyHeaderButton] = useState(false);
	const [showScrollTopButton, toggleScrollTopButton] = useState(false);
	const [showStickyOffersOverview, toggleOfferOverview] = useState(false);
	const searchEngineRef = useRef();

	const flag = useFsFlag("marketing_bandeau_facilite_paiement");
	const flagValue = flag.getValue(false);

	const { shop, brand, envVars } = useContext(AppGlobalsContext);
	const proxyBasenamePrefix = envVars?.PROXY_BASENAME_PREFIX;

	const isBrandSV = brand === BRANDS.SV;
	const isBrandOV = brand === BRANDS.OV;

	const [showStickyRef, isStickyIntersecting, stickyEntry] = useIntersectionObserver();

	const [topRef, isTopIntersecting, topEntry] = useIntersectionObserver();

	const stickyClassname = classNames("product-page__sticky", {
		"product-page__sticky--top": showStickyOffersOverview,
	});

	const openPhotosSlideshow = useCallback(() => {
		sendTagOnOpenProductCarouselDesktop();
		openSlideshow(SLIDE_SHOW_PHOTO_TYPES.PHOTOS);
	}, []);

	useEffect(() => {
		const top = stickyEntry?.boundingClientRect?.top ?? 0;
		const rootTop = stickyEntry?.rootBounds?.top ?? 0;
		const rootBottom = stickyEntry?.rootBounds?.bottom ?? window.innerHeight;

		const leavingFromTop = !isStickyIntersecting && top < rootTop;
		const leavingFromBottom = !isStickyIntersecting && top > rootBottom;

		toggleOfferOverview(leavingFromTop);

		if (leavingFromTop) {
			toggleScrollTopButton(true);
		} else if (leavingFromBottom) {
			toggleScrollTopButton(false);
		}
	}, [isStickyIntersecting, stickyEntry]);

	useEffect(() => {
		const top = topEntry?.boundingClientRect?.top ?? 0;
		const rootTop = topEntry?.rootBounds?.top ?? 0;

		const leavingFromTop = !isTopIntersecting && top < rootTop;

		if (isTopIntersecting) {
			toggleStickyHeaderButton(false);
		} else if (leavingFromTop) {
			toggleStickyHeaderButton(true);
		}
	}, [isTopIntersecting, topEntry]);

	useEffect(() => {
		if (ficheProduit?.notFound) {
			const [currentLanguageCode, currentCountryCode] = currentShop.split("-");
			redirectToShopUrl({
				language: currentLanguageCode,
				country: currentCountryCode,
				proxyBasenamePrefix,
			});
		}
	}, [ficheProduit.notFound, currentShop, proxyBasenamePrefix]);

	const scrollToSearchEngine = useCallback(() => {
		scrollToElement({
			element: searchEngineRef.current,
			offset: 0,
			disableSmoothScroll: false,
		});
	}, []);

	const containsVideo = videos.length > 0;
	const isFlashsale = isFlashsaleProduct(ficheProduit);
	const isDealExpired =
		get(ficheProduit, "expiration.hour") === 0 && get(ficheProduit, "expiration.day") === 0;

	const isScreenBelowMedium = resolution === RESOLUTION.MEDIUM || resolution === RESOLUTION.SMALL;
	const offerWithTransportLabel = prePackageOffers[0]?.pricing?.label;

	const scrollTopButtonClass = classNames({
		"scroll-top-button": true,
		"scroll-top-button--visible": showScrollTopButton,
	});

	const stickyHeaderClass = classNames({
		"product-page__sticky-header": true,
		"product-page__sticky-header--visible": showStickyHeader,
	});

	const handleScrollToTopClick = useCallback(() => {
		window.scrollTo({ behavior: "smooth", left: 0, top: 0 });
	}, []);

	if (ficheProduit.notFound) {
		return null;
	}

	const BLOCKS_TO_EXPAND_BY_DEFAULT = [
		HOTEL_PANEL_INDEX,
		ROOMS_PANEL_INDEX,
		RESTURANTS_PANEL_INDEX,
	];

	if (!ficheProduit.activity || !isEmpty(ficheProduit.activity)) {
		BLOCKS_TO_EXPAND_BY_DEFAULT.push(ACTIVITIES_PANEL_INDEX);
	}

	if (get(ficheProduit, "itinerary.blocks.length", 0) > 0) {
		BLOCKS_TO_EXPAND_BY_DEFAULT.push(`${Number(ACTIVITIES_PANEL_INDEX) + 1}`);
	}

	const productExperience = ficheProduit?.productExperience;
	const isHotelOrResidenceAccommodation =
		productExperience === QUOTATION_PRODUCT_EXPERIENCE.HOTEL ||
		productExperience === QUOTATION_PRODUCT_EXPERIENCE.RESIDENCE;

	const restaurantSectionHeadline = isHotelOrResidenceAccommodation ? (
		<FormattedMessage id={"product.page.restaurant.headline"} />
	) : (
		get(ficheProduit, "restaurant.headline")
	);

	const activitiesLabel = isHotelOrResidenceAccommodation ? (
		<FormattedMessage id={"product.page.activities.label"} />
	) : (
		get(ficheProduit, "activity.label")
	);

	const accommodationTypeHeadline = isHotelOrResidenceAccommodation ? (
		<FormattedMessage id={"product.page.accommodation.type.headline"} />
	) : (
		get(ficheProduit, "accommodation.type.headline")
	);
	const showTimer = !isDealExpired && isFlashsale && brand !== BRANDS.SV && brand !== BRANDS.TK;

	const productPageHeroClass = classNames("product-page__hero", {
		"product-page__hero--expired-deal": isDealExpired,
	});

	return (
		<div className="product-page">
			<PrintHeader showPfsMessage={showPfsMessage} />
			<FicheProduitHeadMetadata
				ficheProduit={ficheProduit}
				desktopSlideShowPhotos={desktopSlideShowPhotos}
			/>
			{isWhiteLabel && isScreenBelowMedium && (
				<div className={stickyHeaderClass}>
					<HeaderContainer
						showMenuItems={true}
						showDialPhone={false}
						showBackBtn={true}
						showMarketingBanner={false}
					/>
				</div>
			)}
			{!isScreenBelowMedium && slideshow.isSlideshowOpened && (
				<SlideShow
					isOpened={slideshow.isSlideshowOpened}
					close={closeSlideshow}
					photos={desktopSlideShowPhotos}
				/>
			)}
			<div className={productPageHeroClass} data-testid="product-page-hero">
				{isDealExpired && (
					<Typography
						variant={TYPOGRAPHY_VARIANTS.XL3}
						variantMD={TYPOGRAPHY_VARIANTS.XL9}
						isBold
						className="hero__text--expired-deal"
					>
						<FormattedMessage id="product.deal.expired.label" />
					</Typography>
				)}

				{/* HERO MEDIA TYPE VIEWS ON MOBILE */}
				{isScreenBelowMedium && mobileSlideshowPhotos.length > 0 && (
					<PhotoCarousel photos={mobileSlideshowPhotos} resolution={resolution} />
				)}
				{/* HERO MEDIA TYPE VIEWS ON DESKTOP */}
				{!isScreenBelowMedium && desktopSlideShowPhotos && desktopSlideShowPhotos[0] && (
					<div className="hero-photo" onClick={openPhotosSlideshow}>
						<div className="hero__image">
							<BackgroundProgressiveImg
								sizes={HERO_IMG_SIZES}
								src={desktopSlideShowPhotos[0].url}
								thumbnail={desktopSlideShowPhotos[0].thumbnail}
								quality="q_auto:best"
								cloudinaryOptions={HERO_IMG_CLOUDINARY_OPTIONS}
								backgroundPosition={"center"}
								productUri={ficheProduit?.uri}
							/>
						</div>
					</div>
				)}

				{isScreenBelowMedium ? (
					<>
						<div className="toolbar">
							<div className="toolbar__item">
								<SocialBarPopover
									position="bottom"
									buttonLabel={<IconShare height={30} />}
									urlToShare={
										typeof window !== "undefined"
											? window.location.origin + window.location.pathname
											: ""
									}
								/>
							</div>
						</div>
						{showTimer && (
							<div className="product-page__deal">
								<DealTimer
									hour={get(ficheProduit, "expiration.hour")}
									day={get(ficheProduit, "expiration.day")}
								/>
							</div>
						)}
					</>
				) : (
					<div className="product-page__toolbar">
						<div className="product-page__container">
							<div className="toolbar">
								<div className="toolbar__item" onClick={openPhotosSlideshow}>
									<IconPicture
										height={20}
										width={20}
										style={{ marginRight: "8px" }}
									/>
									<FormattedPlural
										value={desktopSlideShowPhotos.length}
										one={
											<FormattedMessage
												id="product.page.photo.button.label.singular"
												values={{
													length: desktopSlideShowPhotos.length,
												}}
											/>
										}
										other={
											<FormattedMessage
												id="product.page.photo.button.label.plural"
												values={{
													length: desktopSlideShowPhotos.length,
												}}
											/>
										}
									/>
								</div>

								<div className="toolbar__item">
									<SocialBarPopover
										position="top"
										buttonLabel={
											<div
												style={{
													display: "flex",
													alignItems: "center",
												}}
											>
												<IconShare
													height={25}
													width={20}
													style={{ marginRight: "6px" }}
												/>
												<FormattedMessage id="product.page.map.share.label" />
											</div>
										}
										urlToShare={
											typeof window !== "undefined"
												? window.location.origin + window.location.pathname
												: ""
										}
									/>
								</div>
							</div>
							{showTimer && (
								<div className="product-page__deal">
									<DealTimer
										hour={get(ficheProduit, "expiration.hour")}
										day={get(ficheProduit, "expiration.day")}
									/>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
			<div className="product-page__header">
				<div className="product-page__container">
					<div className="product-page__product-header" ref={topRef}>
						<ProductHeader
							name={ficheProduit.name}
							category={ficheProduit.category}
							categoryType={ficheProduit.categoryType}
							region={ficheProduit.region}
							resort={ficheProduit.resort}
							country={ficheProduit.country}
						/>

						<ProductRatingContainer
							locationName={
								<ProductNameWithCategory
									name={ficheProduit.name}
									category={ficheProduit.category}
								/>
							}
							tripAdvisor={ficheProduit?.tripAdvisor}
							holidayCheck={{ ...ficheProduit?.holidaycheck, size: "small" }}
						/>

						{ficheProduit.badges && ficheProduit.badges.length > 0 && (
							<div className="product-page__badges">
								{ficheProduit.badges?.map((badge, id) => {
									return <ProductBadge key={id} label={badge.label} />;
								})}
							</div>
						)}
					</div>

					{isScreenBelowMedium && (
						<div className="product-page__offers">
							{isDealExpired && (
								<LookAlikeSearchContentContainer productId={ficheProduit?.id} />
							)}

							{flagValue ? (
								<div className="product-page__payment-description">
									<PaymentDescriptionContainer />
								</div>
							) : (
								<TotalCreditBannerContainer />
							)}

							{Number(productPriceValue) > 0 && (
								<div className="product-page__offers-overview">
									<OffersOverview
										offers={prePackageOffers}
										updateBooking={updateBooking}
										allAvailableDurations={allAvailableDurations}
										productPriceValue={productPriceValue}
									/>

									<div className="product-page__available-durations">
										<AvailableDurations durations={allAvailableDurations} />
									</div>
								</div>
							)}
							{negociated?.length > 0 && (
								<div className="product-page__negociated">
									<NegociatedList
										negociatedList={negociated}
										specialOffer={specialOffer}
										offerWithTransportLabel={offerWithTransportLabel}
									/>

									<FullyCustomizableNote />
								</div>
							)}

							{flagValue && <SearchEngineCreditBannerContainer />}

							<BookingBannerPlaceholderContainer disableFlashySpecialOffer />
						</div>
					)}

					{!isScreenBelowMedium && (
						<aside className="product-page__aside" ref={searchEngineRef}>
							<div className="product-page__container product-page__container--aside">
								{!isDealExpired ? (
									<div className="product-page__search-engine">
										<SearchEngineContainer />
									</div>
								) : (
									<LookAlikeSearchContentContainer productId={ficheProduit?.id} />
								)}
								{shop === "en-GB" && (
									<div className="product-page__atol-abta">
										<CondensedBookWithConfidence />
									</div>
								)}
								<div className="product-page__avis-verifies">
									<AvisVerifiesContainer />
								</div>
								{!isDealExpired && (
									<div className="product-page__contact" ref={showStickyRef}>
										<ContactUs productReference={ficheProduit.id} />

										{isBrandOV && (
											<div
												style={{
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													position: "relative",
													marginTop: "30px",
													paddingTop: "15px",
												}}
											>
												<span
													style={{
														position: "absolute",
														top: "0",
														fontSize: "8px",
														textTransform: "uppercase",
														color: "#333",
													}}
												>
													<FormattedMessage id="general.advertising.label" />
												</span>
												<div
													id="oan_ora_1_300x250_voyages.resultat.fichesproduits"
													style={{ width: "300px", height: "250px" }}
												/>
											</div>
										)}
									</div>
								)}
							</div>
						</aside>
					)}
				</div>
			</div>
			<main className="product-page__main">
				<div className="product-page__container product-page__container--main">
					<div>
						{isScreenBelowMedium ? (
							<Collapse
								defaultActiveKey={BLOCKS_TO_EXPAND_BY_DEFAULT}
								expandIcon={({ isActive }) => {
									return (
										<i
											style={{
												transition: "transform .2s ease",
												transform: `rotate(${isActive ? 180 : 0}deg)`,
											}}
											className="icon icon--down-grey-dark"
										/>
									);
								}}
							>
								<Collapse.Panel
									headerClass="fiche-produit-item__header"
									header={
										<Typography
											variant={TYPOGRAPHY_VARIANTS.XL}
											variantMD={TYPOGRAPHY_VARIANTS.XL4}
											isBold
											className="fiche-produit-item__title"
											component="h2"
										>
											{get(ficheProduit, "accommodation.intro.headline")}
										</Typography>
									}
								>
									<div className="fiche-produit-item__body">
										<AccommodationSection
											videoUrl={containsVideo ? videos[0].url : undefined}
											videoType={
												containsVideo ? videos[0].videoType : undefined
											}
											tripAdvisor={get(ficheProduit, "tripAdvisor", {})}
											holidayCheck={get(ficheProduit, "holidaycheck", {})}
											name={ficheProduit.name}
											category={ficheProduit.category}
											categoryType={ficheProduit.categoryType}
											accommodation={ficheProduit.accommodation}
											sanitaryInformations={ficheProduit.sanitaryInformations}
											gpsLat={get(ficheProduit, "gps.lat")}
											gpsLong={get(ficheProduit, "gps.lon")}
											interactiveMap={ficheProduit.interactiveMap}
											googleAPIKey={googleAPIKey}
										/>
									</div>
								</Collapse.Panel>

								{ficheProduit?.accommodation?.amenities?.headline &&
									ficheProduit?.accommodation?.amenities?.groupedAmenities
										?.length > 0 && (
										<Collapse.Panel
											headerClass="fiche-produit-item__header"
											header={
												<Typography
													variant={TYPOGRAPHY_VARIANTS.XL}
													variantMD={TYPOGRAPHY_VARIANTS.XL4}
													isBold
													className="fiche-produit-item__title"
													component="h2"
												>
													{
														ficheProduit?.accommodation?.amenities
															?.headline
													}
												</Typography>
											}
										>
											<div className="fiche-produit-item__body">
												<Amenities
													amenities={
														ficheProduit?.accommodation?.amenities
															?.groupedAmenities
													}
												/>
											</div>
										</Collapse.Panel>
									)}

								{get(ficheProduit, "itinerary.blocks.length", 0) > 0 && (
									<Collapse.Panel
										headerClass="fiche-produit-item__header"
										header={
											<Typography
												variant={TYPOGRAPHY_VARIANTS.XL}
												variantMD={TYPOGRAPHY_VARIANTS.XL4}
												isBold
												className="fiche-produit-item__title"
												component="h2"
											>
												{get(ficheProduit, "itinerary.headline")}
											</Typography>
										}
									>
										<div className="fiche-produit-item__body">
											<AdditionalSections
												blocks={get(ficheProduit, "itinerary.blocks")}
											/>
										</div>
									</Collapse.Panel>
								)}
								{get(ficheProduit, "accommodation.type.headline") && (
									<Collapse.Panel
										headerClass="fiche-produit-item__header"
										header={
											<Typography
												variant={TYPOGRAPHY_VARIANTS.XL}
												variantMD={TYPOGRAPHY_VARIANTS.XL4}
												isBold
												className="fiche-produit-item__title"
												component="h2"
											>
												{get(ficheProduit, "accommodation.type.headline")}
											</Typography>
										}
									>
										<div className="fiche-produit-item__body">
											<AccommodationTypes
												paragraphs={get(
													ficheProduit,
													"accommodation.type.paragraphs"
												)}
												photo={get(
													ficheProduit,
													"accommodation.type.photo"
												)}
												accommodationTypes={get(
													ficheProduit,
													"accommodation.type.accommodationTypes"
												)}
												facilities={get(
													ficheProduit,
													"accommodation.type.facilities"
												)}
												productUri={ficheProduit?.uri}
											/>
										</div>
									</Collapse.Panel>
								)}
								{get(ficheProduit, "restaurant.headline") && !isBrandSV && (
									<Collapse.Panel
										headerClass="fiche-produit-item__header"
										header={
											<Typography
												variant={TYPOGRAPHY_VARIANTS.XL}
												variantMD={TYPOGRAPHY_VARIANTS.XL4}
												isBold
												className="fiche-produit-item__title"
												component="h2"
											>
												{get(ficheProduit, "restaurant.headline")}
											</Typography>
										}
									>
										<div className="fiche-produit-item__body">
											<RestaurantSection
												intro={get(ficheProduit, "restaurant.intro")}
												restaurants={get(
													ficheProduit,
													"restaurant.restaurants"
												)}
												facilities={get(
													ficheProduit,
													"restaurant.facilities"
												)}
												boards={ficheProduit.boards}
											/>
										</div>
									</Collapse.Panel>
								)}
								{(!ficheProduit.activity || !isEmpty(ficheProduit.activity)) && (
									<Collapse.Panel
										headerClass="fiche-produit-item__header"
										header={
											<Typography
												variant={TYPOGRAPHY_VARIANTS.XL}
												variantMD={TYPOGRAPHY_VARIANTS.XL4}
												isBold
												className="fiche-produit-item__title"
												component="h2"
											>
												{get(ficheProduit, "activity.label")}
											</Typography>
										}
									>
										<div className="fiche-produit-item__body">
											<Activities
												intro={get(ficheProduit, "activity.intro")}
												facilities={get(
													ficheProduit,
													"activity.facilities"
												)}
												activities={get(
													ficheProduit,
													"activity.activities"
												)}
												description={get(
													ficheProduit,
													"activity.description"
												)}
											/>
										</div>
									</Collapse.Panel>
								)}

								{ficheProduit.additionalSections &&
									ficheProduit.additionalSections.map(additionalSection => {
										return (
											<Collapse.Panel
												key={additionalSection.headline}
												headerClass="fiche-produit-item__header"
												header={
													<Typography
														variant={TYPOGRAPHY_VARIANTS.XL}
														variantMD={TYPOGRAPHY_VARIANTS.XL4}
														isBold
														className="fiche-produit-item__title"
														component="h2"
													>
														{additionalSection.headline}
													</Typography>
												}
											>
												<div className="fiche-produit-item__body">
													<AdditionalSections
														moreDetails={additionalSection.moreDetails}
														blocks={additionalSection.blocks}
													/>
												</div>
											</Collapse.Panel>
										);
									})}

								{!isEmpty(ficheProduit.destination) && (
									<Collapse.Panel
										headerClass="fiche-produit-item__header"
										header={
											<Typography
												variant={TYPOGRAPHY_VARIANTS.XL}
												variantMD={TYPOGRAPHY_VARIANTS.XL4}
												isBold
												className="fiche-produit-item__title"
												component="h2"
											>
												<FormattedMessage
													id="destination.discover"
													values={{
														destination: get(
															ficheProduit,
															"destination.label"
														),
													}}
												/>
											</Typography>
										}
									>
										<div className="fiche-produit-item__body">
											<Destination
												paragraphs={get(
													ficheProduit,
													"destination.paragraphs"
												)}
												photo={get(ficheProduit, "destination.photo")}
												video={get(ficheProduit, "destination.video")}
												showVideoOnModal={!isScreenBelowMedium}
											/>
										</div>
									</Collapse.Panel>
								)}

								{get(ficheProduit, "transportPartnership.headline") && (
									<Collapse.Panel
										headerClass="fiche-produit-item__header"
										header={
											<Typography
												variant={TYPOGRAPHY_VARIANTS.XL}
												variantMD={TYPOGRAPHY_VARIANTS.XL4}
												isBold
												className="fiche-produit-item__title"
												component="h2"
											>
												{get(ficheProduit, "transportPartnership.headline")}
											</Typography>
										}
									>
										<div className="fiche-produit-item__body">
											<TransportPartnership
												intro={ficheProduit.transportPartnership.intro}
												logo={ficheProduit.transportPartnership.logo}
												paragraphs={
													ficheProduit.transportPartnership.paragraphs
												}
												photo={ficheProduit.transportPartnership.photo}
												video={ficheProduit.transportPartnership.video}
											/>
										</div>
									</Collapse.Panel>
								)}

								<Collapse.Panel
									headerClass="fiche-produit-item__header"
									header={
										<Typography
											variant={TYPOGRAPHY_VARIANTS.XL}
											variantMD={TYPOGRAPHY_VARIANTS.XL4}
											isBold
											className="fiche-produit-item__title"
											component="h2"
										>
											<FormattedMessage id="useful.information.title" />
										</Typography>
									}
								>
									<div className="fiche-produit-item__body">
										<UsefulInformation
											partnerCode={partnerCode}
											usefulInformations={ficheProduit.usefulInformations}
										/>
									</div>
								</Collapse.Panel>
							</Collapse>
						) : (
							<>
								<TotalCreditBannerContainer />

								{negociated?.length > 0 && (
									<div className="product-page__bloc product-page__bloc--highlighted">
										<NegociatedList
											negociatedList={negociated}
											specialOffer={specialOffer}
											offerWithTransportLabel={offerWithTransportLabel}
										/>
										<FullyCustomizableNote />
									</div>
								)}

								<div className="product-page__bloc">
									<AccommodationSection
										headline={get(ficheProduit, "accommodation.intro.headline")}
										videoUrl={containsVideo ? videos[0].url : undefined}
										videoType={containsVideo ? videos[0].videoType : undefined}
										displayVideo={!isBrandSV}
										tripAdvisor={get(ficheProduit, "tripAdvisor", {})}
										holidayCheck={get(ficheProduit, "holidaycheck", {})}
										name={ficheProduit.name}
										category={ficheProduit.category}
										categoryType={ficheProduit.categoryType}
										accommodation={ficheProduit.accommodation}
										sanitaryInformations={ficheProduit.sanitaryInformations}
										gpsLat={get(ficheProduit, "gps.lat")}
										gpsLong={get(ficheProduit, "gps.lon")}
										interactiveMap={ficheProduit.interactiveMap}
										googleAPIKey={googleAPIKey}
									/>
								</div>

								{get(ficheProduit, "itinerary.blocks.length", 0) > 0 && (
									<div className="product-page__bloc">
										<AdditionalSections
											headline={get(ficheProduit, "itinerary.headline")}
											moreDetails={get(ficheProduit, "itinerary.moreDetails")}
											blocks={get(ficheProduit, "itinerary.blocks")}
										/>
									</div>
								)}
								{get(ficheProduit, "accommodation.type.headline") && (
									<div className="product-page__bloc">
										<AccommodationTypes
											headline={accommodationTypeHeadline}
											paragraphs={get(
												ficheProduit,
												"accommodation.type.paragraphs"
											)}
											photo={get(ficheProduit, "accommodation.type.photo")}
											accommodationTypes={get(
												ficheProduit,
												"accommodation.type.accommodationTypes"
											)}
											facilities={get(
												ficheProduit,
												"accommodation.type.facilities"
											)}
											productUri={ficheProduit?.uri}
										/>
									</div>
								)}

								{ficheProduit?.accommodation?.amenities?.headline &&
									ficheProduit?.accommodation?.amenities?.groupedAmenities
										?.length > 0 && (
										<div className="product-page__bloc">
											<Amenities
												headline={
													ficheProduit?.accommodation?.amenities?.headline
												}
												amenities={
													ficheProduit?.accommodation?.amenities
														?.groupedAmenities
												}
											/>
										</div>
									)}

								{get(ficheProduit, "restaurant.headline") && !isBrandSV && (
									<div className="product-page__bloc">
										<RestaurantSection
											headline={restaurantSectionHeadline}
											intro={get(ficheProduit, "restaurant.intro")}
											restaurants={get(
												ficheProduit,
												"restaurant.restaurants"
											)}
											facilities={get(ficheProduit, "restaurant.facilities")}
											boards={ficheProduit.boards}
										/>
									</div>
								)}

								{(!ficheProduit.activity || !isEmpty(ficheProduit.activity)) && (
									<div className="product-page__bloc">
										<Activities
											label={activitiesLabel}
											intro={get(ficheProduit, "activity.intro")}
											facilities={get(ficheProduit, "activity.facilities")}
											activities={get(ficheProduit, "activity.activities")}
											description={get(ficheProduit, "activity.description")}
										/>
									</div>
								)}

								{ficheProduit.additionalSections &&
									ficheProduit.additionalSections.map(additionalSection => {
										return (
											<div
												className="product-page__bloc"
												key={additionalSection.headline}
											>
												<AdditionalSections
													headline={additionalSection.headline}
													blocks={additionalSection.blocks}
												/>
											</div>
										);
									})}

								{!isEmpty(ficheProduit.destination) && (
									<div className="product-page__bloc">
										<Destination
											title={<FormattedMessage id="destination.discover" />}
											paragraphs={get(ficheProduit, "destination.paragraphs")}
											photo={get(ficheProduit, "destination.photo")}
											video={get(ficheProduit, "destination.video")}
											showVideoOnModal={!isScreenBelowMedium}
										/>
									</div>
								)}

								{!isEmpty(ficheProduit.destination?.importantInfo) && (
									<div className="product-page__bloc">
										<Destination
											title={
												<FormattedMessage id="product.page.destination.important.information.title" />
											}
											paragraphs={get(
												ficheProduit,
												"destination.importantInfo"
											)}
										/>
									</div>
								)}

								{get(ficheProduit, "transportPartnership.headline") && (
									<div className="product-page__bloc">
										<TransportPartnership
											headline={ficheProduit.transportPartnership.headline}
											intro={ficheProduit.transportPartnership.intro}
											logo={ficheProduit.transportPartnership.logo}
											paragraphs={
												ficheProduit.transportPartnership.paragraphs
											}
											photo={ficheProduit.transportPartnership.photo}
											video={ficheProduit.transportPartnership.video}
										/>
									</div>
								)}
							</>
						)}
					</div>

					{!isScreenBelowMedium && !isDealExpired && (
						<div>
							<aside className={stickyClassname}>
								<div className="product-page__sticky-content">
									<AsideOffersOverview
										offers={prePackageOffers}
										hideTimer={!showTimer}
										hour={ficheProduit?.expiration?.hour}
										day={ficheProduit?.expiration?.day}
										onClick={scrollToSearchEngine}
										itemClickable={false}
										productPriceValue={productPriceValue}
									/>

									{shop === "en-GB" && (
										<div className="product-page__atol-abta">
											<CondensedBookWithConfidence />
										</div>
									)}

									<div className="product-page__avis-verifies">
										<AvisVerifiesContainer />
									</div>

									<div className="product-page__contact">
										<ContactUs productReference={ficheProduit.id} />
									</div>
								</div>
							</aside>
						</div>
					)}
				</div>

				<footer className="product-page__footer">
					{!isDealExpired && isScreenBelowMedium && (
						<>
							<div className="product-page__avis-verifies">
								<AvisVerifiesContainer />
							</div>
							<div className="product-page__contact">
								<ContactUs productReference={ficheProduit.id} />
							</div>
						</>
					)}

					{shouldFetchLookAlikeProduts && !isDealExpired && (
						<div className="product-page__lookalike">
							<LookAlikeProductsContainer productId={ficheProduit.id} />
						</div>
					)}
				</footer>
			</main>
			<div className="product-page__bottom-bar">
				{!isDealExpired && isScreenBelowMedium && <ProductActionButtonMobile />}
			</div>
			<div className="product-page__included">
				{prePackageOffers.map(offer => {
					const offerPricingType = get(offer, "pricing.type");

					return (
						<div key={offerPricingType} className="product-page__included__item">
							<div className="included">
								<div className="included__header">
									{get(offer, "pricing.label")}
								</div>
								<div className="included__list">
									<div className="included__title">
										<FormattedMessage id="price.detail.included.title">
											{msg => <span>{`${msg} :`}</span>}
										</FormattedMessage>
									</div>
									<List
										items={get(ficheProduit, `included.${offerPricingType}`)}
										enableHTMLFormatting={true}
									/>
								</div>
								<div className="included__list">
									<div className="included__title">
										<FormattedMessage id="price.detail.not.included.title">
											{msg => <span>{`${msg} :`}</span>}
										</FormattedMessage>
									</div>
									<List
										items={get(ficheProduit, `notIncluded.${offerPricingType}`)}
										enableHTMLFormatting={true}
									/>
								</div>
							</div>
						</div>
					);
				})}
			</div>
			{!isEmpty(ficheProduit.usefulInformations) && !isScreenBelowMedium && (
				<div className="product-page__content">
					<div id="useful-information" className="product-page__information">
						<Collapse
							defaultActiveKey={["0"]}
							expandIcon={({ isActive }) => {
								return (
									<IconArrowDown
										style={{
											transition: "transform .2s ease",
											transform: `rotate(${isActive ? 180 : 0}deg)`,
										}}
									/>
								);
							}}
						>
							<Collapse.Panel
								headerClass="fiche-produit-item__header"
								header={
									<Typography
										variant={TYPOGRAPHY_VARIANTS.XL}
										variantMD={TYPOGRAPHY_VARIANTS.XL4}
										isBold
										className="fiche-produit-item__title"
										component="h2"
									>
										<FormattedMessage id="useful.information.title" />
									</Typography>
								}
							>
								<div className="fiche-produit-item__body">
									<UsefulInformation
										usefulInformations={ficheProduit.usefulInformations}
									/>
								</div>
							</Collapse.Panel>
						</Collapse>
					</div>
				</div>
			)}

			<div className="product-page__rtb">
				<ReasonToBookBlockContainer />
			</div>

			<PushNotificationContainer />
			<span className={scrollTopButtonClass} onClick={handleScrollToTopClick}>
				<IconGoToTopArrow width={50} height={50} />
			</span>
		</div>
	);
};

FicheProduit.propTypes = {
	ficheProduit: ficheProduitPropTypes,
	prePackageOffers: PropTypes.array,
	slideshow: PropTypes.shape({
		isSlideshowOpened: PropTypes.bool,
		photosType: PropTypes.string,
	}),
	negociated: PropTypes.array,
	allAvailableDurations: PropTypes.arrayOf(PropTypes.number),
	videos: PropTypes.arrayOf(videoPropTypes),
	mobileSlideshowPhotos: PropTypes.arrayOf(photoPropTypes),
	desktopSlideShowPhotos: PropTypes.arrayOf(photoPropTypes),
	closeSlideshow: PropTypes.func,
	currentShop: PropTypes.string,
	openSlideshow: PropTypes.func,
	resolution: PropTypes.string,
	partnerCode: PropTypes.string,
	shouldFetchLookAlikeProduts: PropTypes.bool,
	isWhiteLabel: PropTypes.bool,
	specialOffer: PropTypes.object,
	googleAPIKey: PropTypes.string,
	showPfsMessage: PropTypes.bool,
	productPriceValue: PropTypes.string,
	updateBooking: PropTypes.func,
};

export default memo(FicheProduit);
